import { GridRenderCellParams, GridTreeNodeWithRender, GridValueFormatterParams } from '@mui/x-data-grid'
import type { FC, ReactNode } from 'react'
import { dayjsCustom } from '../utils'
import { MapTableBaseContext } from './context'
import { BtnDetail, BtnFormDetail } from './ui.buttons'
import { Breadcrumbs, IBreadcrumbConfig } from '../components'
import { CreateStatusCell, IStatusCellOptions, IStatusCellConfig } from './ui.units'
import { CellBase, CellImageSmall, CellBaseOptions, CellChips, CellChipsProps, CellDate, CellDatePropsOwner } from './ui.units'
import ToolbarPannel, { IToolbarPannelProps } from './toolbar-pannel'
import TableHelper from './helpers'
import CreateTable from './create.table'
import CreateActionRow from './create.action-row'
import { TypographyProps } from '@mui/material'
import { generateExportCsvOptions } from './csv-export-helper'

const formatDateString = 'MM/DD/YYYY HH:mm'

type RenderCellBaseParams<T> = Omit<CellBaseOptions, 'imageUrl'> & {
  imageUrl?: (value: any, model: T) => string
  valueFormatter?: (value: any, model: T) => string
  icon?: ReactNode | ((value: any, model: T) => ReactNode)
  typographyPropsGetter?: (value: any, model: T) => TypographyProps & { [key: string]: any }
}

interface FormatterDateOptions {
  formatString?: string
  showRelative?: boolean
}

class DinoTableBase {
  //#region Base

  createTable = CreateTable

  createActionRow = CreateActionRow

  generateExportCsvOptions = generateExportCsvOptions

  setUrlQuery = TableHelper.setUrlQuery

  getUrlQuery = TableHelper.getUrlQuery

  mapContext = MapTableBaseContext

  ToolbarPannel = ToolbarPannel

  createToolbar = (props: IToolbarPannelProps): FC<IToolbarPannelProps> => {
    return (p) => <ToolbarPannel {...{ ...p, ...props }} />
  }

  BtnFormDetail = BtnFormDetail

  BtnDetail = BtnDetail

  createBreadcrumbConfigs = (value: IBreadcrumbConfig[]) => value

  createBreadcrumbs = (value: IBreadcrumbConfig[]): FC => {
    return () => <Breadcrumbs value={value} />
  }

  createStatusCell = CreateStatusCell
  //#endregion

  //#region Value Formatter
  formatterDate = (value: any, options?: FormatterDateOptions): string => {
    const f = options?.formatString ?? formatDateString
    const showRelative = options?.showRelative ?? false
    try {
      if (!value) return ''

      const date = dayjsCustom(value)
      const formatted = date.format(f)

      return showRelative ? `${formatted} (${dayjsCustom().to(date)})` : formatted
    } catch (error) {
      return ''
    }
  }

  valueFormatterDate = (options?: FormatterDateOptions) => {
    return (params: GridValueFormatterParams<any>) => this.formatterDate(params.value, options)
  }
  //#endregion

  //#region Render Cell
  renderCellChips = (params?: CellChipsProps) => {
    return function func(tableRow: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>) {
      return <CellChips {...params} value={tableRow.value ?? params?.value} />
    }
  }

  renderCellStatus = function <E extends string>(config: IStatusCellConfig<E>, options?: IStatusCellOptions) {
    const StatusCellInstance = CreateStatusCell(config, { sx: { minWidth: '90px' }, ...options })
    return function func(tableRow: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>) {
      return <StatusCellInstance value={tableRow.value} />
    }
  }

  renderCellBase = function <T = any>(options?: RenderCellBaseParams<T>) {
    return function func(tableRow: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>) {
      const { imageUrl, valueFormatter, typographyProps, typographyPropsGetter, ...p } = options ?? {}
      const value = valueFormatter ? valueFormatter(tableRow.value, tableRow.row) : undefined
      const img = imageUrl ? imageUrl(tableRow.value, tableRow.row) : undefined
      const icon = options?.icon ? (typeof options.icon === 'function' ? options.icon(tableRow.value, tableRow.row) : options.icon) : undefined
      const mergedTypographyProps = typographyPropsGetter ? typographyPropsGetter(tableRow.value, tableRow.row) : typographyProps
      return <CellBase value={tableRow.value} valueFormatted={value} {...p} imageUrl={img} icon={icon} typographyProps={mergedTypographyProps} />
    }
  }

  renderCellImage = function <T>(selectImage: (value: T) => T[keyof T], options?: { fallbackSrc?: string }) {
    return function func(tableRow: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>) {
      return <CellImageSmall value={tableRow.value} imageUri={selectImage(tableRow.row)?.toString()} fallbackSrc={options?.fallbackSrc} />
    }
  }

  renderCellDate = function (params?: CellDatePropsOwner) {
    return function func(tableRow: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>) {
      const { formatString, ...props } = params ?? {}
      return <CellDate {...props} value={tableRow.value} formatString={formatString ?? formatDateString} />
    }
  }
  //#endregion
}

const DinoTable = new DinoTableBase()

export default DinoTable
